import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Switch from "@mui/material/Switch";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Button, TextField, Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  deletePoster,
  getGamesData,
  getGame,
  saveGameSettings,
  getLinks,
  loadPoster,
  getProfile,
} from "../redux/asyncThunk/userActions";
import InputFileUpload from "./UploadFile";
import { formatDateTimeRu } from "../utils/formatDate";
import { sanitizePlainText } from "../utils/inputValidation";
import {
  EXAMPLE_START_POST,
  getProjectStatusFromSettings,
  settingsFromProjectStatus,
  mergeGameSettingsWithGamification,
} from "../utils/projectAccess";
import { readGamificationFromSettings } from "../utils/gamification";
import GamificationSettings from "./GamificationSettings";
import { readChatAccessGateFromSettings, DEFAULT_CHAT_ACCESS_GATE } from "../utils/chatAccessGate";
import { deriveLegacyMessenger } from "../utils/messengers";
import { getContentTypeLabel, resolveGameContentType } from "../utils/contentType";
import ProjectTeamAccess from "./ProjectTeamAccess";
import LaunchReadinessChecklist from "./LaunchReadinessChecklist";
import { readAvailableMessengersFromSettings } from "./ProjectMessengerAvailability";
import { userSlice } from "../redux/user/userSlice";
import { AUTH_FIELD_SX } from "../pages/AuthLayout";
import { getServerMediaUrl } from "../utils/mediaUrl";
import { buildStartPostPreview } from "../utils/startPostPreview";
import "./index.css";

const { addMessage } = userSlice;

const fieldSx = { ...AUTH_FIELD_SX, maxWidth: "100%", width: "100%" };

const DESCRIPTION_PLACEHOLDER =
  "Этот сценарий поможет освоить вам ремонт кофемашины Макита. Вы изучите как починить и не сломать.";

const SETTINGS_STEPS = [
  {
    id: "start",
    label: "Стартовый пост",
    hint: "Что увидит сотрудник при открытии ссылки",
  },
  {
    id: "publish",
    label: "Публикация",
    hint: "Статус проекта и повторное прохождение",
  },
  {
    id: "gamification",
    label: "Мотивация",
    hint: "XP, рейтинг и достижения — по желанию",
  },
  {
    id: "access",
    label: "Доступ для команды",
    hint: "Ссылки для Telegram и MAX — отправьте их сотрудникам",
  },
];

export default function Settings() {
  const dispatch = useDispatch();
  const game = useSelector((state) => state.user.game);
  const [data, setData] = useState();
  const [activeStep, setActiveStep] = useState("start");
  const [linkCopiedTick, setLinkCopiedTick] = useState(0);
  const [saveState, setSaveState] = useState("idle");
  const [chatAccessGate, setChatAccessGate] = useState(DEFAULT_CHAT_ACCESS_GATE);
  const [settings, setSettings] = useState({
    title: "",
    category: "",
    program: "",
    description: "",
    private_access: false,
    allow_replay: true,
    availableMessengers: ["telegram", "max"],
    projectStatus: "draft",
    gamification: readGamificationFromSettings(game?.settings),
  });

  const user = useSelector((state) => state.user?.data?.user);
  const profile = useSelector((state) => state.user?.profile);

  useEffect(() => {
    if (user?.id && !profile?.login) {
      dispatch(getProfile({ id: user.id }));
    }
  }, [dispatch, user?.id, profile?.login]);

  useEffect(() => {
    if (!game) return;

    setSettings({
      title: game.title ?? "",
      category: game.category ?? "",
      program: game.program ?? "",
      description: game.description ?? "",
      private_access: false,
      allow_replay: game.settings?.allow_replay !== false,
      availableMessengers: readAvailableMessengersFromSettings(game.settings),
      projectStatus: getProjectStatusFromSettings(game.settings),
      gamification: readGamificationFromSettings(game.settings),
    });
    setChatAccessGate(readChatAccessGateFromSettings(game.settings));
    setData({
      ...game,
      scenesValue: game?.nodes?.filter((el) => el.type === "group")?.length,
    });
  }, [game]);

  const handleChange = (event, property) => {
    setSettings((prev) => ({
      ...prev,
      [property]:
        typeof event.target.value === "string"
          ? sanitizePlainText(
              event.target.value,
              property === "description" ? 2000 : 200
            )
          : event.target.value,
    }));
  };

  const handleToggle = (property) => (event) => {
    setSettings((prev) => ({
      ...prev,
      [property]: event.target.checked,
    }));
  };

  const buildSettingsPayload = () => {
    const statusFlags = settingsFromProjectStatus(
      settings.projectStatus,
      game?.settings
    );

    return mergeGameSettingsWithGamification(game?.settings, {
      private_access: false,
      published: statusFlags.published,
      hidden: statusFlags.hidden,
      completion_text: game?.settings?.completion_text ?? "",
      author_telegram: game?.settings?.author_telegram ?? "",
      allow_replay: settings.allow_replay,
      availableMessengers: settings.availableMessengers,
      messenger: deriveLegacyMessenger(settings.availableMessengers),
      contentType: resolveGameContentType(game),
      gamification: settings.gamification,
      ...chatAccessGate,
    });
  };

  const handleUpdateSettings = async () => {
    setSaveState("saving");

    try {
      await dispatch(
        saveGameSettings({
          game: game._id,
          title: settings.title,
          description: settings.description,
          category: settings.category,
          program: settings.program,
          settings: buildSettingsPayload(),
          onSucces: () => {
            dispatch(getLinks({ userId: user.id }));
            dispatch(getGame({ gameId: game._id }));
          },
        })
      ).unwrap();
      setSaveState("saved");
      window.setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("idle");
    }
  };

  const handleAddPoster = (event, id) => {
    const file = event.target.files[0];
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = async () => {
      const base64String = reader.result.split(",")[1];
      dispatch(
        loadPoster({
          gameId: id,
          base64String,
          onSucces: () => dispatch(getGame({ gameId: game._id })),
        })
      );
    };
  };

  const handleShowExamplePost = () => {
    const preview = buildStartPostPreview({
      ...game,
      title: settings.title || game?.title,
      description: settings.description || game?.description,
      poster: data?.poster ?? game?.poster,
    });

    dispatch(
      addMessage({
        type: "start-post",
        preview: preview ?? EXAMPLE_START_POST,
        label: "Стартовый пост",
      })
    );
  };

  const posterTitle = settings.title || "Название вашего сценария";
  const posterDescription =
    settings.description || DESCRIPTION_PLACEHOLDER;
  const activeStepMeta =
    SETTINGS_STEPS.find((step) => step.id === activeStep) ?? SETTINGS_STEPS[0];
  const stepIndex = SETTINGS_STEPS.findIndex((step) => step.id === activeStep);

  return (
    <div className="app-editor-settings">
      <div className="app-editor-settings__shell">
        <aside className="app-editor-settings__aside">
          <LaunchReadinessChecklist
            game={game}
            draftSettings={settings}
            linkCopiedVersion={linkCopiedTick}
            compact
            collapsible
            onStepFocus={setActiveStep}
          />

          <section className="app-settings-card app-settings-card--meta-compact">
            <div className="app-settings-meta-compact">
              <div className="app-settings-meta-compact__row">
                <span className="app-settings-field__label">Тип</span>
                <span className="app-settings-type-badge">
                  {getContentTypeLabel(resolveGameContentType(game))}
                </span>
              </div>
              <div className="app-settings-meta-compact__row">
                <span className="app-settings-field__label">Изменён</span>
                <span className="app-settings-meta">
                  {formatDateTimeRu(game?.updatedAt || game?.createdAt)}
                </span>
              </div>
              <div className="app-settings-meta-compact__row app-settings-meta-compact__row--id">
                <span className="app-settings-field__label">ID</span>
                <span className="app-settings-id">{game?._id}</span>
              </div>
            </div>
          </section>
        </aside>

        <div className="app-editor-settings__main">
          <nav className="app-settings-steps" aria-label="Шаги настройки">
            {SETTINGS_STEPS.map((step, index) => (
              <button
                key={step.id}
                type="button"
                className={`app-settings-steps__item${
                  step.id === activeStep ? " app-settings-steps__item--active" : ""
                }${index < stepIndex ? " app-settings-steps__item--done" : ""}`}
                onClick={() => setActiveStep(step.id)}
              >
                <span className="app-settings-steps__num">{index + 1}</span>
                <span className="app-settings-steps__label">{step.label}</span>
              </button>
            ))}
          </nav>

          <section className="app-settings-card app-settings-card--step">
            <header className="app-settings-step__head">
              <h3 className="app-settings-card__title app-settings-card__title--left">
                {activeStepMeta.label}
              </h3>
              <p className="app-settings-step__hint">{activeStepMeta.hint}</p>
            </header>

            {activeStep === "start" ? (
              <div className="app-settings-poster-grid app-settings-poster-grid--step">
                <div className="app-settings-poster-fields">
                  <div className="app-settings-field">
                    <label className="app-settings-field__label" htmlFor="title">
                      Название
                    </label>
                    <TextField
                      onChange={(event) => handleChange(event, "title")}
                      id="title"
                      placeholder="Название вашего сценария"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={settings?.title ?? ""}
                      sx={fieldSx}
                    />
                  </div>

                  <div className="app-settings-field">
                    <label className="app-settings-field__label" htmlFor="program">
                      Программа / отдел
                    </label>
                    <TextField
                      onChange={(event) => handleChange(event, "program")}
                      id="program"
                      placeholder="Например: Онбординг, Отдел продаж"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={settings?.program ?? ""}
                      sx={fieldSx}
                    />
                  </div>

                  <div className="app-settings-field">
                    <label
                      className="app-settings-field__label"
                      htmlFor="description"
                    >
                      Текст стартового поста
                    </label>
                    <TextField
                      id="description"
                      placeholder={DESCRIPTION_PLACEHOLDER}
                      variant="outlined"
                      multiline
                      minRows={3}
                      fullWidth
                      value={settings?.description ?? ""}
                      onChange={(event) => handleChange(event, "description")}
                      sx={fieldSx}
                    />
                  </div>

                  <div className="app-settings-poster__actions">
                    <InputFileUpload
                      handleAddPoster={(event) => handleAddPoster(event, game._id)}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      className="app-btn app-btn--secondary app-btn--compact"
                      startIcon={<VisibilityOutlinedIcon />}
                      onClick={handleShowExamplePost}
                    >
                      Пример стартового поста
                    </Button>
                  </div>
                </div>

                <div className="app-settings-poster">
                  {data?.poster ? (
                    <Tooltip title="Удалить постер" placement="top">
                      <DeleteIcon
                        className="app-settings-poster__delete"
                        onClick={() => {
                          dispatch(
                            deletePoster({
                              gameId: game._id,
                              onSucces: () =>
                                dispatch(getGamesData({ id: user.id })),
                            })
                          );
                        }}
                      />
                    </Tooltip>
                  ) : null}

                  <div className="app-settings-poster__media">
                    {data?.poster ? (
                      <img src={getServerMediaUrl(data.poster)} alt="Постер сценария" />
                    ) : (
                      <div className="app-settings-poster__empty">
                        <ImageNotSupportedOutlinedIcon />
                        <span>Загрузите изображение</span>
                      </div>
                    )}
                  </div>

                  <div className="app-settings-poster__caption">
                    <p className="app-settings-poster__caption-title">
                      Название: {posterTitle}
                    </p>
                    <p>{posterDescription}</p>
                  </div>
                </div>
              </div>
            ) : null}

            {activeStep === "publish" ? (
              <div className="app-settings-publish">
                <div className="app-settings-field app-settings-field--status">
                  <span className="app-settings-field__label">Статус проекта</span>
                  <RadioGroup
                    row
                    className="app-settings-status"
                    value={settings.projectStatus}
                    onChange={(event) => handleChange(event, "projectStatus")}
                  >
                    <FormControlLabel
                      value="published"
                      control={<Radio size="small" />}
                      label="Опубликован"
                      className="app-settings-status__option"
                    />
                    <FormControlLabel
                      value="draft"
                      control={<Radio size="small" />}
                      label="В работе"
                      className="app-settings-status__option"
                    />
                    <FormControlLabel
                      value="hidden"
                      control={<Radio size="small" />}
                      label="Остановлен"
                      className="app-settings-status__option"
                    />
                  </RadioGroup>
                </div>

                <div className="app-settings-toggle">
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={settings.allow_replay}
                        onChange={handleToggle("allow_replay")}
                      />
                    }
                    label="Разрешить повторное прохождение"
                  />
                  <p className="app-settings-toggle__hint">
                    Если выключено, клиент не сможет пройти сценарий заново.
                  </p>
                </div>
              </div>
            ) : null}

            {activeStep === "gamification" ? (
              <GamificationSettings
                embedded
                value={settings.gamification}
                onChange={(gamification) =>
                  setSettings((prev) => ({ ...prev, gamification }))
                }
              />
            ) : null}

            {activeStep === "access" ? (
              <ProjectTeamAccess
                embedded
                gameId={game?._id}
                value={settings.availableMessengers}
                onChange={(next) =>
                  setSettings((prev) => ({
                    ...prev,
                    availableMessengers: next,
                  }))
                }
                chatAccessSettings={chatAccessGate}
                onChatAccessChange={setChatAccessGate}
                onShareLinkCopied={() => setLinkCopiedTick((value) => value + 1)}
                hintExtra={
                  <>
                    {" "}
                    <Link to="/main/price">Тарифы</Link>
                  </>
                }
              />
            ) : null}

            <footer className="app-settings-step__footer">
              <div className="app-settings-step__nav">
                {stepIndex > 0 ? (
                  <Button
                    size="small"
                    variant="outlined"
                    className="app-btn app-btn--secondary app-btn--compact"
                    onClick={() => setActiveStep(SETTINGS_STEPS[stepIndex - 1].id)}
                  >
                    Назад
                  </Button>
                ) : (
                  <span />
                )}
                {stepIndex < SETTINGS_STEPS.length - 1 ? (
                  <Button
                    size="small"
                    variant="contained"
                    className="app-btn app-btn--primary app-btn--compact"
                    onClick={() => setActiveStep(SETTINGS_STEPS[stepIndex + 1].id)}
                  >
                    Дальше
                  </Button>
                ) : null}
              </div>
              <Button
                variant="contained"
                size="small"
                className="app-btn app-btn--primary app-btn--compact"
                disabled={saveState === "saving"}
                onClick={handleUpdateSettings}
              >
                {saveState === "saving"
                  ? "Сохранение…"
                  : saveState === "saved"
                    ? "Сохранено"
                    : "Сохранить настройки"}
              </Button>
            </footer>
          </section>
        </div>
      </div>
    </div>
  );
}
